import { useMemo, useState } from 'react'
import { Box, Download, Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import clsx from 'clsx'

function formatBytes(n) {
  if (!n && n !== 0) return '—'
  const num = Number(n)
  if (num < 1024) return `${num} B`
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`
  return `${(num / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '—'
  }
}

export default function RoomScanCard({ scan, onPreview, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const dims = scan.dimensions_meters || {}
  const dimLabel = useMemo(() => {
    const l = Number(dims.length)
    const w = Number(dims.width)
    const h = Number(dims.height)
    if (![l, w, h].every((v) => Number.isFinite(v) && v > 0)) return null
    return `${l.toFixed(1)} × ${w.toFixed(1)} × ${h.toFixed(1)} m`
  }, [dims])

  const previewSrc = scan.floorplan_png_url || scan.thumbnail_url

  return (
    <article className="room-scan-card">
      <button
        type="button"
        className="room-scan-card__thumb"
        onClick={() => onPreview?.(scan)}
        aria-label={`Preview ${scan.name}`}
      >
        {previewSrc ? (
          <img src={previewSrc} alt="" />
        ) : (
          <div className="room-scan-card__thumb-fallback">
            <Box size={28} aria-hidden />
          </div>
        )}
      </button>

      <div className="room-scan-card__body">
        <div className="room-scan-card__title-row">
          <h3 className="room-scan-card__title" title={scan.name}>
            {scan.name}
          </h3>
          <div className="room-scan-card__menu-wrap">
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              aria-label="Actions"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div className="room-scan-card__menu" role="menu" onMouseLeave={() => setMenuOpen(false)}>
                <button
                  type="button"
                  className="room-scan-card__menu-item"
                  onClick={() => {
                    setMenuOpen(false)
                    onPreview?.(scan)
                  }}
                >
                  <Eye size={14} /> Preview
                </button>
                <a
                  className="room-scan-card__menu-item"
                  href={scan.download_url || '#'}
                  download
                  onClick={() => setMenuOpen(false)}
                >
                  <Download size={14} /> Download USDZ
                </a>
                <button
                  type="button"
                  className="room-scan-card__menu-item"
                  onClick={() => {
                    setMenuOpen(false)
                    onRename?.(scan)
                  }}
                >
                  <Pencil size={14} /> Rename
                </button>
                <button
                  type="button"
                  className={clsx(
                    'room-scan-card__menu-item',
                    'room-scan-card__menu-item--danger',
                  )}
                  onClick={() => {
                    setMenuOpen(false)
                    onDelete?.(scan)
                  }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="room-scan-card__meta">
          <span>{formatDate(scan.captured_at)}</span>
          <span className="dot" aria-hidden>·</span>
          <span>{formatBytes(scan.file_size)}</span>
          {dimLabel && (
            <>
              <span className="dot" aria-hidden>·</span>
              <span>{dimLabel}</span>
            </>
          )}
        </div>

        <ul className="room-scan-card__counts">
          <li>
            <span>{scan.wall_count ?? 0}</span> walls
          </li>
          <li>
            <span>{scan.door_count ?? 0}</span> doors
          </li>
          <li>
            <span>{scan.window_count ?? 0}</span> windows
          </li>
          <li>
            <span>{scan.object_count ?? 0}</span> objects
          </li>
        </ul>
      </div>
    </article>
  )
}
